import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { theme } from '../../../styles/theme';
import Table from '../../../common/table';
import Page from '../../../common/Page';
import Loader from '../../../common/Loader';
import { getEventDetail, saveEventNotes } from '../../../common/api';
import { formatEventDateTime } from '../../../common/dates';
import { parseEventString, displayEventName, safeBackTarget } from '../../../common/events';
import NotesModal from '../../../common/NotesModal';
import DeleteEventButton from '../../../common/DeleteEventButton';
import { useCurrentUser, isSuperAdmin } from '../../../common/UserContext';
import { copyToClipboard } from '../../../common/clipboard';

function Event() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const event = queryParams.get('event') || '';
  const readOnly = queryParams.get('readonly') === '1';
  const backTarget = safeBackTarget(queryParams.get('back'));
  const { eventName, when } = parseEventString(event);
  const user = useCurrentUser();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [eventInfo, setEventInfo] = useState({});
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [originalNotes, setOriginalNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        if (event) {
          const jsonDataEvent = await getEventDetail(event);
          setData(jsonDataEvent.tickets || []);
          setEventInfo(jsonDataEvent.event || {});
          const eventNotes = jsonDataEvent.event?.notes || '';
          setNotes(eventNotes);
          setOriginalNotes(eventNotes);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Errore nel caricamento dell\'evento');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [event]);

  const handleNotesClick = () => {
    setIsNotesModalOpen(true);
  };

  const handleNotesModalClose = () => {
    setNotes(originalNotes);
    setIsNotesModalOpen(false);
  };

  const handleNotesSave = async () => {
    setNotesSaving(true);
    try {
      const eventKey = `${eventName}_${when}`;
      await saveEventNotes(eventKey, notes);
      toast.success('Note salvate correttamente');
      setOriginalNotes(notes);
      setIsNotesModalOpen(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Errore nel salvare le note');
    } finally {
      setNotesSaving(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  const totals = {
    totale: data.length,
    validati: data.filter((t) => t.Validato).length,
    pagati: data.filter((t) => t.Pagamento && !t.GiftCard).length,
    giftcard: data.filter((t) => t.GiftCard).length,
  };
  const hasNotes = Boolean(notes && notes.trim());
  // Stato della notifica di ritiro: solo per gli eventi passati (vista in sola lettura)
  const notificationSent = eventInfo.emailDeliverySent === true;
  const notificationSentAt = eventInfo.emailDeliverySentAt ? formatEventDateTime(eventInfo.emailDeliverySentAt) : null;
  const canSendNotification = readOnly && !notificationSent && isSuperAdmin(user) && eventInfo.eventKey;

  return (
    <Page>
      <div style={styles.content}>
        <div style={styles.contentHeader}>
          <div style={styles.backButtonContainer}>
            <button onClick={() => navigate(backTarget)} style={styles.backArrow} aria-label="Indietro">
              ←
            </button>
            {readOnly && <span style={styles.readOnlyBadge}>Sola lettura</span>}
          </div>
          <h4 style={styles.eventTitle}>{displayEventName(eventName)}</h4>
          <div style={styles.dateText}>{formatEventDateTime(when)}</div>
          {eventInfo.where && <div style={styles.whereText}>{eventInfo.where}</div>}

          {readOnly && (
            <div style={notificationSent ? styles.notificationSent : styles.notificationPending}>
              <strong>Notifica di ritiro:</strong>{' '}
              {notificationSent
                ? `inviata${notificationSentAt ? ` il ${notificationSentAt}` : ''}`
                : 'non inviata'}
              {canSendNotification && (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/withdrawal/event?eventKey=${encodeURIComponent(eventInfo.eventKey)}`)}
                  style={styles.notificationLink}
                >
                  Invia ora
                </button>
              )}
            </div>
          )}

          <div style={styles.countersRow}>
            <Counter label="Totale" value={totals.totale} />
            <Counter label="Validati" value={totals.validati} />
            <Counter label="Pagati" value={totals.pagati} />
            <Counter label="Giftcard" value={totals.giftcard} />
          </div>

          {!readOnly && (
            <div style={styles.notesButtonContainer}>
              <button
                onClick={handleNotesClick}
                style={hasNotes ? styles.notesButtonWithNotes : styles.notesButton}
              >
                Note
              </button>
            </div>
          )}
          {readOnly && hasNotes && (
            <div style={styles.notesReadOnly}>
              <strong>Note:</strong> {notes}
            </div>
          )}

          <Table
            data={data}
            event={event}
            readOnly={readOnly}
          />

          <div style={styles.linkButtonsRow}>
            <CopyLinkButton label="Copia Stripe Link" value={eventInfo.stripeLink} />
            <CopyLinkButton label="Copia Giftcard Link" value={eventInfo.giftcardLink} />
          </div>
          <DeleteEventButton eventKey={eventInfo.eventKey} eventName={eventName} backTarget={backTarget} />
        </div>
      </div>

      {!readOnly && isNotesModalOpen && (
        <NotesModal
          onClose={handleNotesModalClose}
          notes={notes}
          setNotes={setNotes}
          onSave={handleNotesSave}
          saving={notesSaving}
        />
      )}
    </Page>
  );
}

// Bottone piccolo che copia un link negli appunti; disabilitato se il link non è valorizzato
function CopyLinkButton({ label, value }) {
  const disabled = !value;

  const handleCopy = async () => {
    try {
      await copyToClipboard(value);
      toast.success('Link copiato');
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Impossibile copiare il link');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={disabled}
      title={disabled ? 'Link non disponibile' : value}
      style={disabled ? styles.copyButtonDisabled : styles.copyButton}
    >
      {label}
    </button>
  );
}

function Counter({ label, value }) {
  return (
    <div style={styles.counter}>
      <div style={styles.counterValue}>{value}</div>
      <div style={styles.counterLabel}>{label}</div>
    </div>
  );
}

const styles = {
  content: {
    backgroundColor: theme.colors.white,
    padding: '0px',
    borderRadius: theme.borderRadius,
    boxShadow: theme.shadows.main,
    color: theme.colors.text,
    width: '100%',
    maxWidth: '1000px',
    marginBottom: '25px',
  },
  contentHeader: {
    width: '100%',
    textAlign: 'center',
  },
  eventTitle: {
    color: theme.colors.text,
    marginBottom: 15,
    fontSize: '20px',
  },
  notesButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '15px',
  },
  notesButton: {
    padding: '5px 15px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  notesButtonWithNotes: {
    padding: '5px 15px',
    backgroundColor: '#FF0000',
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  linkButtonsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '0 15px 25px 15px',
  },
  copyButton: {
    padding: '6px 12px',
    backgroundColor: theme.colors.white,
    color: theme.colors.brand,
    border: `1px solid ${theme.colors.brand}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  copyButtonDisabled: {
    padding: '6px 12px',
    backgroundColor: theme.colors.white,
    color: theme.colors.textSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontSize: '12px',
  },
  notesReadOnly: {
    margin: '0 auto 15px auto',
    padding: '10px 15px',
    maxWidth: '700px',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    backgroundColor: theme.colors.background,
    borderRadius: '4px',
    fontSize: '14px',
  },
  dateText: {
    fontSize: 15,
    color: theme.colors.text,
    marginBottom: '5px',
  },
  whereText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: '10px',
  },
  notificationSent: {
    display: 'inline-block',
    margin: '0 auto 10px auto',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#e6f4ea',
    color: '#1e6b34',
  },
  notificationPending: {
    display: 'inline-block',
    margin: '0 auto 10px auto',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fdecec',
    color: '#8a1c1c',
  },
  notificationLink: {
    marginLeft: '10px',
    padding: '3px 10px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  countersRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    margin: '10px 0 15px 0',
  },
  counter: {
    minWidth: '80px',
    padding: '8px 12px',
    borderRadius: theme.borderRadius,
    backgroundColor: theme.colors.background,
  },
  counterValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.colors.brand,
  },
  counterLabel: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
  },
  backArrow: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: theme.colors.brand,
  },
  backButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 10px',
  },
  readOnlyBadge: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '2px 10px',
  },
};

export default Event;
